import {setAmyIsMutedState} from '../../services/amy/amyControlService';
import scubeContext from '../../core/scubeContext';
import {AudioContext as safeAudioContext} from 'standardized-audio-context';
import {sceneEffectType} from './scenes/effects';
import {sceneCameraType} from './scenes/cameras';
import {sceneEventType} from './sceneEvents';
import {amy3DHolderType} from './scene3DTypes';
import {autobind} from '../../shared/typescript/autobind';

export default class sceneContext {
  curSequence: sceneSequenceType | undefined = undefined;
  currentScene: sceneType | undefined = undefined;
  amy3DHolder: amy3DHolderType = undefined as unknown as amy3DHolderType; //web gl scene and models
  eventListeners: ((sceneEvent: sceneEventType) => void)[] = [];

  invokeSetSize: ((width: number, height: number) => void) | undefined = undefined;
  onAnimationFinished = undefined;
  invokeAnimationStart = undefined;
  invokeFadeToAnimation: ((animationName: string) => void) | undefined = undefined;

  startSequence(sequence: sceneSequenceType) {
    this.curSequence = sequence; //zatial iba jedna sequence v jednom case
    //console.log(this);
    sequence.onStart(this);
  }
  fadeToAnimation(animationName: string) {
    //console.log(this);
    this.invokeFadeToAnimation?.(animationName);
  }
  startEffect(effect: sceneEffectType) {
    effect.onStart(this);
  }
  startCamera(camera: sceneCameraType) {
    camera.onStart(this);
  }
  animationFinished(animationName: string) {
    this.curSequence?.onAnimationFinished(this, animationName);
  }
  setSize(width: number, height: number) {
    this.invokeSetSize?.(width, height);
  }

  invokeCameraFinished(camera: sceneCameraType) {
    this.curSequence?.onCameraFinished(this, camera);
  }
  //invoke events
  invokeEffectFinished(effect: sceneEffectType) {
    this.curSequence?.onEffectFinished(this, effect);
  }
  //process event in amy webgl
  invokeEvent(sceneEvent: sceneEventType) {
    if (this.currentScene && this.curSequence) {
      this.curSequence.onEvent(this, sceneEvent);
      this.currentScene.onEvent(this, sceneEvent);
    }
  }
  //send event outside of amy
  pushEvent(sceneEvent: sceneEventType) {
    for (let i = 0; i < this.eventListeners.length; i++) {
      this.eventListeners[i](sceneEvent);
    }
  }
}

let audioCtx = new safeAudioContext(); //use npm new (window.AudioContext || window.webkitAudioContext)();
//console.log(audioCtx.state);
if (audioCtx.state === 'suspended') {
  //console.log('stale suspended');
  scubeContext.isAmyAudioSuspended = true;
  scubeContext.isAmyMuted = true;
}

audioCtx.onstatechange = function () {
  //console.log('zmena; ' + audioCtx.state);

  if (audioCtx.state === 'suspended') {
    scubeContext.isAmyAudioSuspended = true;
    scubeContext.isAmyMuted = true;
  } else {
    scubeContext.isAmyAudioSuspended = false;
  }
};

let gainNode = audioCtx.createGain();
gainNode.connect(audioCtx.destination);

let lastAudioSource: any = undefined;
let disableSpeech = false;

export type sceneSequenceType = {
  onStart: (context: sceneContext | any) => void;
  onFinish: (context: sceneContext | any) => void;
  onAnimationFinished: (context: sceneContext | any, animationName: string) => void;
  onEffectFinished: (context: sceneContext | any, effect: sceneEffectType) => void;
  onCameraFinished: (context: sceneContext | any, camera: sceneCameraType) => void;
  onEvent: (context: sceneContext | any, sequenceEvent: sceneEventType) => void;
  isRecurrent: boolean;
};

export type sceneType = {
  modelName: string;
  id: string;
  name: string;
  onStart: (context: sceneContext) => void;
  onStop: (context: sceneContext) => void;
  onFinish: (context: sceneContext) => void;
  onSequenceFinished: (sequenceName: string) => void;
  onEffectFinished: (effectname: string) => void;
  onEvent: (context: sceneContext, sceneEvent: sceneEventType) => void;
  sequences: sceneSequenceType[];
  amySettings: {
    particlesHitArea: boolean;
    particlesRandom: number;
    particlesDepth: number;
    particlesXYMove: boolean;
    particlesBW: boolean;
    particlesSquare: boolean;
    particlesSize: number;
    particlesSmooth: number;
    particlesShift: number;
    matrix: number;
    planeRotate: number;
  };
};

export const sceneEngine = {
  curSceneContext: {} as sceneContext,
  amy3DHolder: undefined as amy3DHolderType | undefined,

  init(scene: sceneType, amyModel: string) {
    scene.modelName = amyModel;
    this.curSceneContext = new sceneContext();
    this.curSceneContext.currentScene = scene;
  },
  startScene() {
    //console.log('scene started');
    this.curSceneContext?.currentScene?.onStart(this.curSceneContext);
  },
  stopScene() {
    this.curSceneContext?.currentScene?.onStop(this.curSceneContext);
  },
  pushEvent(sceneEvent: sceneEventType) {
    if (this.curSceneContext) {
      this.curSceneContext.invokeEvent(sceneEvent);
    }
  },

  animationFinished(animationName: string) {
    this.curSceneContext?.animationFinished(animationName);
  },

  setSize(width: number, height: number) {
    this.curSceneContext?.setSize(width, height);
  },

  subscribeListen(eventListener: (sceneEvent: sceneEventType) => void) {
    this.curSceneContext?.eventListeners.push(eventListener);
  },

  unSubscribeListen(eventListener: (sceneEvent: sceneEventType) => void) {
    const index = this.curSceneContext?.eventListeners.indexOf(eventListener);
    if (index && index > -1) {
      this.curSceneContext?.eventListeners.splice(index, 1);
    }
  },

  stopSpeech() {
    if (lastAudioSource) {
      try {
        lastAudioSource.stop(0);
      } catch (e) {}
      try {
        lastAudioSource.disconnect(gainNode);
      } catch (e) {}
      lastAudioSource = undefined;
    }
  },

  disableSpeech() {
    disableSpeech = true;

    if (lastAudioSource) {
      try {
        lastAudioSource.stop(0);
      } catch (e) {}
      try {
        lastAudioSource.disconnect(gainNode);
      } catch (e) {}
      lastAudioSource = undefined;
    }

    this.publishAmySpeechChanged();
  },

  enableSpeech() {
    disableSpeech = false;

    this.publishAmySpeechChanged();
  },

  initMuteValue(isMuted: boolean) {
    if (isMuted) gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    else gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
  },
  unSuspend() {
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
      scubeContext.isAmyAudioSuspended = false;
      scubeContext.isAmyMuted = false; //start speek on resume
    }

    //console.log(scubeContext);
  },
  toggleMute() {
    //console.log(scubeContext);

    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
      scubeContext.isAmyAudioSuspended = false;
      scubeContext.isAmyMuted = false; //start speek on resume
    } else {
      scubeContext.isAmyMuted = !scubeContext.isAmyMuted;
    }

    //console.log(scubeContext);

    if (scubeContext.isAmyMuted) {
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    } else {
      gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
    }

    setAmyIsMutedState(scubeContext.isAmyMuted);

    this.publishAmySpeechChanged();

    //console.log({ suspended: scubeContext.isAmyAudioSuspended, muted: scubeContext.isAmyMuted });

    return scubeContext.isAmyMuted;
  },
  handlersAmySpeech: [] as any[],
  handlerAmySpeechCounter: 0,
  subscribeAmySpeech(handler: (data: any) => void) {
    //this.handlers.push(handler);
    //nameof<baseServiceInterface>(x=>x.forceRender);
    const key = this.handlerAmySpeechCounter++;
    const keyString = key.toString();

    (this.handlersAmySpeech as any)[keyString] = handler;
    return () => this.unsubscribeAmySpeech(keyString, handler);
  },
  unsubscribeAmySpeech(key: string, handler: (data: any) => void) {
    delete (this.handlersAmySpeech as any)[key];
  },
  publishAmySpeechChanged() {
    const values = Object.values(this.handlersAmySpeech);
    for (let i = 0; i < values.length; i++) {
      (values[i] as any)({});
    }
  },

  // mute() {
  //   //console.log('mute');
  //   gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
  //   scubeContext.isAmyMuted = true;
  //   setAmyIsMutedState(true);
  // },
  //
  // unmute() {
  //   //console.log('unmute');
  //   if (audioCtx.state === 'suspended') {
  //     audioCtx.resume();
  //     scubeContext.isAmyAudioSuspended = false;
  //   }
  //   gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
  //   scubeContext.isAmyMuted = false;
  //   setAmyIsMutedState(false);
  // },

  playSpeech(speechData: string | undefined, onSpeechStarted: () => void, onSpeechFinished: () => void) {
    if (!scubeContext.isAmyAudioSuspended && speechData && !disableSpeech) {
      //console.log('amy speech');

      fetch(`data:application/octet-stream;base64,${speechData}`)
        .then((res) => {
          return res.blob();
        })
        .then((blob) => {
          return blob.arrayBuffer();
        })
        .then((audioData) => {
          //console.log(audioData);

          let useDelay = false;

          if (lastAudioSource) {
            useDelay = true;
            lastAudioSource.stop(0);
            lastAudioSource.disconnect(gainNode);
            lastAudioSource = undefined;
          }

          //console.log('1-' + new Date().valueOf());
          audioCtx.decodeAudioData(audioData, function (buffer) {
            if (lastAudioSource) {
              useDelay = true;
              lastAudioSource.stop(0);
              lastAudioSource.disconnect(gainNode);
              lastAudioSource = undefined;
            }

            //console.log(lastAudioSource);

            lastAudioSource = audioCtx.createBufferSource();
            //lastAudioSource.loop = true;
            //console.log(lastAudioSource);

            lastAudioSource.addEventListener('ended', () => {
              scubeContext.isAmySpeaking = false;
              if (onSpeechFinished) onSpeechFinished();
            });

            lastAudioSource.buffer = buffer;
            if (useDelay) {
              try {
                lastAudioSource.connect(gainNode);

                scubeContext.isAmySpeaking = true;
                if (onSpeechStarted) onSpeechStarted();

                lastAudioSource.start(audioCtx.currentTime + 0.8);
              } catch (e) {}
            } else {
              try {
                lastAudioSource.connect(gainNode);

                scubeContext.isAmySpeaking = true;
                if (onSpeechStarted) onSpeechStarted();
                lastAudioSource.start(0);
              } catch (e) {}
            }
          });
        });
    }

    //     let audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    //
    //     let source = audioCtx.createBufferSource();
    // // set the buffer in the AudioBufferSourceNode
    //     source.buffer = myArrayBuffer;
    // // connect the AudioBufferSourceNode to the
    // // destination so we can hear the sound
    //     source.connect(audioCtx.destination);
    // // start the source playing
    //     source.start();
  },
};

autobind(sceneEngine);
